/**
 * @file OrderItemUnlinkedNotice.tsx
 * @description Блок «позиция без товара каталога» внутри строки состава
 * (phase19.24 шаг 5, OQ-54).
 *
 * Отдельным компонентом, а не куском разметки строки: ту же проблему
 * показывают и строка на десктопе, и карточка на телефоне, и текст обязан
 * совпадать слово в слово (реестр `lib/order-item-link.ts`).
 *
 * Действие подаётся по правилам mobile-first: на `lg` — кнопка прямо в
 * строке, на `<lg` — пункт в «⋯» (нижняя шторка с тач-целями ≥56px). Двух
 * кнопок на узком экране быть не должно — карточка и так уезжает за край.
 */

import { Link2 } from "lucide-react";

import { OwnerChip } from "./OwnerChip";
import { ORDER_ITEM_UNLINKED_TEXT } from "../../lib/order-item-link";
import { Button } from "../ui/button";
import { RowActionsMenu } from "../ui/RowActionsMenu";

interface Props {
  /** Право SHOP_MANAGE_ORDERS: без него — только объяснение, без кнопки. */
  canRelink: boolean;
  /** Идёт запрос по составу — контролы гаснут. */
  busy: boolean;
  onRelink: () => void;
}

export function OrderItemUnlinkedNotice({ canRelink, busy, onRelink }: Props) {
  return (
    <div className="mt-2 flex flex-col gap-2 rounded-lg bg-destructive/10 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {/* Красный стикер из общего реестра: «Нужно решение» читается так же,
            как в полосе «Сейчас» и в карточке реестра заказов. */}
        <OwnerChip alert="PROBLEM" label={ORDER_ITEM_UNLINKED_TEXT.chip} />
        <span className="min-w-0 text-sm text-destructive">
          {ORDER_ITEM_UNLINKED_TEXT.row}
        </span>
      </div>

      {canRelink ? (
        <div className="shrink-0">
          <Button
            variant="outline"
            className="hidden min-h-11 lg:inline-flex"
            disabled={busy}
            onClick={onRelink}
          >
            <Link2 className="size-4" aria-hidden />
            {ORDER_ITEM_UNLINKED_TEXT.action}
          </Button>
          <div className="lg:hidden">
            <RowActionsMenu
              title="Действия с позицией"
              ariaLabel="Действия с позицией"
              disabled={busy}
              actions={[
                {
                  key: "relink",
                  label: ORDER_ITEM_UNLINKED_TEXT.action,
                  icon: Link2,
                  run: onRelink,
                },
              ]}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
